/*******************************************************************************
 *
 * Shared branch-name environment variable contract.
 *
 * `GIT_BRANCH_NAME` is injected at two layers that must agree:
 *
 * 1. ABOVE turbo — the root `build:all` projen task env. This is the process
 *    env turbo itself runs in, so it is the only layer turbo's hasher can read
 *    when a task declares `GIT_BRANCH_NAME` in its `env: []` array.
 * 2. BELOW turbo — the `cdk synth` step env inside a CDK app's `synth` /
 *    `synth:silent` tasks, which is what actually resolves the branch used in
 *    generated stack/resource names.
 *
 * Layer 1 sets the value that layer 2's `[ -n "$GIT_BRANCH_NAME" ]` guard then
 * consumes, so the two fallbacks must be identical — otherwise the value turbo
 * hashes and the value synth bakes into the assembly can diverge. Both layers
 * import the constants below so they agree by construction.
 *
 ******************************************************************************/

/**
 * Environment variable carrying the current git branch name.
 */
export const GIT_BRANCH_NAME_ENV = "GIT_BRANCH_NAME";

/**
 * Projen task env value that resolves {@link GIT_BRANCH_NAME_ENV}.
 *
 * The value must be exactly `$(...)` so projen's `evalEnvironment` expands it
 * (substitution only fires when the full value matches `$(...)`), and the body
 * must NOT contain a nested `$(...)` — projen's built-in cross-platform shell
 * (0.101+) cannot evaluate command substitution and throws
 * `Not implemented: command`.
 *
 * The `[ -n "$GIT_BRANCH_NAME" ]` guard honors a pre-set (CI-exported)
 * value for remote-cache parity, otherwise falls back to
 * `git branch --show-current`. That fallback returns empty on a detached HEAD,
 * where CI's job-level env takes precedence anyway.
 */
export const GIT_BRANCH_NAME_ENV_VALUE =
  '$([ -n "$GIT_BRANCH_NAME" ] && echo "$GIT_BRANCH_NAME" || git branch --show-current)';
